// iter60 — Meeting Templates authoring UI with live preview.
// Renders under Admin › "Meeting templates" tab. Controllers of TruPath fanned
// templates to every org via iter58 backend; here they author + edit + preview.

import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit3, Trash2, ClipboardList, Eye, Video, X } from "lucide-react";
import { formatDate } from "@/lib/format";

const STYLES = [
  { key: "management_review", label: "Management Review" },
  { key: "safety",             label: "Safety" },
  { key: "quality",            label: "Quality" },
  { key: "toolbox_talk",       label: "Toolbox Talk" },
  { key: "team_huddle",        label: "Team Huddle" },
  { key: "shift_handover",     label: "Shift Handover" },
  { key: "custom",             label: "Custom" },
];

// iter61 — template categories for grouping. Free-form 'custom' allowed too.
const CATEGORIES = [
  { key: "safety",   label: "Safety",   color: "#e63946" },
  { key: "quality",  label: "Quality",  color: "#1a5fb4" },
  { key: "ops",      label: "Operations", color: "#1a8754" },
  { key: "hr",       label: "HR / People", color: "#a13ad6" },
  { key: "finance",  label: "Finance",  color: "#d6a900" },
  { key: "audit",    label: "Audit / Compliance", color: "#ff4500" },
  { key: "custom",   label: "Custom",   color: "#1a1a1a" },
];

const EMPTY = {
  title: "",
  style: "management_review",
  description: "",
  category: "custom",
  tags: [],
  default_agenda: [],
  default_attendee_role_ids: [],
  default_vv_item_ids: [],
  duration_minutes: 60,
  is_public: true,
};

export default function MeetingTemplatesTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // template object being edited (null = new)
  const [preview, setPreview] = useState(null);   // template being previewed
  const [categoryFilter, setCategoryFilter] = useState("all");   // iter61

  const load = () => {
    setBusy(true);
    api.get("/meeting-templates")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);

  // iter61 — group by category (bucketed count for filter chips)
  const byCategory = useMemo(() => {
    const g = { all: rows.length };
    for (const t of rows) {
      const key = t.category || "custom";
      g[key] = (g[key] || 0) + 1;
    }
    return g;
  }, [rows]);
  const visible = categoryFilter === "all" ? rows : rows.filter((t) => (t.category || "custom") === categoryFilter);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (t) => setEditing({ ...EMPTY, ...t });
  const doDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.title}"? This does NOT delete meetings created from it.`)) return;
    try {
      await api.delete(`/meeting-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="meeting-templates-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] font-bold">// Meeting Templates</div>
          <h2 className="font-display font-black text-xl leading-tight">Author reusable meetings</h2>
          <p className="text-[12px] text-[#1a1a1a]/70 mt-1 max-w-2xl">
            Build a template once, everyone in the org can start from it. Templates authored by the TruPath master org
            are cloned into every other org automatically — this list shows YOUR org&apos;s copies (edit freely).
          </p>
        </div>
        <button data-testid="mtpl-new-btn" onClick={openNew} className="btn-pill btn-pill-primary text-[12px]">
          <Plus className="w-3.5 h-3.5" /> New template
        </button>
      </div>

      {/* iter61 — category filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="mtpl-category-filter">
        <button onClick={() => setCategoryFilter("all")}
          className={`btn-pill text-[10px] ${categoryFilter === "all" ? "btn-pill-primary" : "btn-pill-secondary"}`}
          data-testid="mtpl-cat-all">
          All ({byCategory.all || 0})
        </button>
        {CATEGORIES.map((c) => (
          <button key={c.key}
            onClick={() => setCategoryFilter(c.key)}
            data-testid={`mtpl-cat-${c.key}`}
            className={`btn-pill text-[10px] ${categoryFilter === c.key ? "btn-pill-primary" : "btn-pill-secondary"}`}
            style={categoryFilter === c.key ? { backgroundColor: c.color, borderColor: c.color } : {}}>
            {c.label} ({byCategory[c.key] || 0})
          </button>
        ))}
      </div>

      <div className="surface-card rounded-2xl overflow-hidden">
        {busy && rows.length === 0 ? (
          <div className="p-8 text-center text-[12px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-[12px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">
            {rows.length === 0 ? <>No templates yet. Click &quot;New template&quot; to build one.</> : "No templates in this category."}
          </div>
        ) : (
          <table className="w-full text-[12px] table-fixed">
            <colgroup>
              <col />
              <col className="w-[15%]" />
              <col className="w-[15%]" />
              <col className="w-[9%]" />
              <col className="w-[10%]" />
              <col className="w-[12%]" />
              <col className="w-[130px]" />
            </colgroup>
            <thead className="bg-[#1a1a1a] text-white">
              <tr>
                <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Title</th>
                <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Category</th>
                <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Style</th>
                <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Agenda</th>
                <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Duration</th>
                <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Source</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1a1a1a]/06">
              {visible.map((t) => {
                const cat = CATEGORIES.find((c) => c.key === (t.category || "custom"));
                return (
                <tr key={t.id} data-testid={`mtpl-row-${t.id}`} className="hover:bg-[#f7f5f2]/50 align-top">
                  <td className="px-2 py-2.5 font-medium break-words">{t.title}</td>
                  <td className="px-2 py-2.5">
                    {cat && (
                      <span
                        className="inline-block font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold text-white"
                        style={{ backgroundColor: cat.color }}
                        data-testid={`mtpl-cat-pill-${t.id}`}
                      >
                        {cat.label}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 font-mono text-[10px] uppercase tracking-wider text-[#1a5fb4]">
                    {STYLES.find((s) => s.key === t.style)?.label || t.style}
                  </td>
                  <td className="px-2 py-2.5 font-mono text-[10px] text-[#1a1a1a]/70">
                    {(t.default_agenda || []).length} item{(t.default_agenda || []).length === 1 ? "" : "s"}
                  </td>
                  <td className="px-2 py-2.5 font-mono text-[10px] text-[#1a1a1a]/70">{t.duration_minutes || 60} min</td>
                  <td className="px-2 py-2.5 text-[10px]">
                    {t.cloned_from_master_id ? (
                      <span className="inline-block bg-[#a13ad6]/15 text-[#a13ad6] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold">TruPath library</span>
                    ) : (
                      <span className="inline-block bg-[#1a1a1a]/08 text-[#1a1a1a]/70 font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full">Org-authored</span>
                    )}
                  </td>
                  <td className="px-1 py-2.5 whitespace-nowrap">
                    <button onClick={() => setPreview(t)} data-testid={`mtpl-preview-${t.id}`}
                      className="p-1.5 text-[#1a1a1a]/60 hover:text-[#1a5fb4]" title="Live preview">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => openEdit(t)} data-testid={`mtpl-edit-${t.id}`}
                      className="p-1.5 text-[#1a1a1a]/60 hover:text-[#ff4500]" title="Edit">
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => doDelete(t)} data-testid={`mtpl-delete-${t.id}`}
                      className="p-1.5 text-[#1a1a1a]/60 hover:text-red-600" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <TemplateEditorDialog
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {preview && (
        <TemplatePreviewDialog template={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}

// ─── Editor dialog with live-preview pane ──────────────────────────────
function TemplateEditorDialog({ template, onClose, onSaved }) {
  const isNew = !template?.id;
  const [form, setForm] = useState({ ...EMPTY, ...template });
  const [agendaTitle, setAgendaTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Template title is required");
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        style: form.style,
        description: form.description || "",
        category: form.category || "custom",
        tags: form.tags || [],
        default_agenda: form.default_agenda || [],
        default_attendee_role_ids: form.default_attendee_role_ids || [],
        default_vv_item_ids: form.default_vv_item_ids || [],
        duration_minutes: Number(form.duration_minutes) || 60,
        is_public: !!form.is_public,
      };
      if (isNew) await api.post("/meeting-templates", payload);
      else       await api.patch(`/meeting-templates/${template.id}`, payload);
      toast.success(isNew ? "Template created — fanning out to other orgs." : "Template updated.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const addAgenda = () => {
    const t = agendaTitle.trim();
    if (!t) return;
    setForm({ ...form, default_agenda: [...(form.default_agenda || []), t] });
    setAgendaTitle("");
  };
  const removeAgenda = (i) => setForm({ ...form, default_agenda: form.default_agenda.filter((_, ix) => ix !== i) });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-5xl rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="mtpl-editor">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] font-bold">
            // {isNew ? "New template" : "Edit template"}
          </div>
          <DialogTitle className="font-display font-black text-xl">
            {isNew ? "Author a meeting template" : form.title}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* LEFT: editor */}
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 font-mono">Title *</label>
              <input data-testid="mtpl-title" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Monthly Management Review — TruPath Standard"
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 font-mono">Meeting style</label>
                <select data-testid="mtpl-style" value={form.style}
                  onChange={(e) => setForm({ ...form, style: e.target.value })}
                  className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm bg-white">
                  {STYLES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 font-mono">Duration (min)</label>
                <input data-testid="mtpl-duration" type="number" min="15" max="480"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                  className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 font-mono">Category</label>
              <select data-testid="mtpl-category" value={form.category || "custom"}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm bg-white">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 font-mono">Description / purpose</label>
              <textarea data-testid="mtpl-desc" value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. ISO 9001 §9.3 monthly management review — inputs, KPIs, action follow-up."
                rows={3}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 font-mono flex items-center gap-1">
                <ClipboardList className="w-3 h-3" /> Baked-in agenda items ({(form.default_agenda || []).length})
              </label>
              <ul className="mt-2 space-y-1.5">
                {(form.default_agenda || []).map((title, i) => (
                  <li key={i} data-testid={`mtpl-agenda-${i}`}
                    className="flex items-start justify-between gap-2 p-2 rounded-xl bg-[#faf8f4] border border-[#1a1a1a]/08 text-[12px]">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="font-mono font-bold text-[10px] text-[#1a1a1a]/50">#{i + 1}</span>
                      <span className="text-[#1a1a1a]">{title}</span>
                    </div>
                    <button type="button" onClick={() => removeAgenda(i)}
                      data-testid={`mtpl-agenda-remove-${i}`}
                      className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0">
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex items-center gap-1.5">
                <input
                  data-testid="mtpl-agenda-input"
                  value={agendaTitle}
                  onChange={(e) => setAgendaTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAgenda(); } }}
                  placeholder="Agenda item title (Enter to add)"
                  className="flex-1 border border-[#1a1a1a]/15 rounded-lg px-2 py-1.5 text-[12px]"
                />
                <button type="button" onClick={addAgenda} data-testid="mtpl-agenda-add"
                  disabled={!agendaTitle.trim()}
                  className="btn-pill btn-pill-secondary text-[10px] disabled:opacity-40">
                  + Add
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT: live preview */}
          <div className="lg:border-l lg:border-[#1a1a1a]/10 lg:pl-5" data-testid="mtpl-preview-pane">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#1a5fb4] font-bold mb-2 flex items-center gap-1">
              <Eye className="w-3 h-3" /> Live Preview — how attendees will see this meeting
            </div>
            <PreviewCard template={form} />
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-secondary text-[11px]">Cancel</button>
          <button data-testid="mtpl-save" onClick={submit} disabled={busy}
            className="btn-pill btn-pill-primary text-[11px]">
            {busy ? "Saving…" : (isNew ? "Create template" : "Save changes")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Preview modal (standalone from list) ──────────────────────────────
function TemplatePreviewDialog({ template, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="mtpl-preview-dialog">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1a5fb4] font-bold">// Template preview</div>
          <DialogTitle className="font-display font-black text-lg">{template.title}</DialogTitle>
        </DialogHeader>
        <PreviewCard template={template} />
        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-secondary text-[11px]">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewCard({ template }) {
  const styleLabel = useMemo(() => STYLES.find((s) => s.key === template.style)?.label || template.style, [template.style]);
  const agenda = template.default_agenda || [];
  return (
    <div className="rounded-2xl overflow-hidden border border-[#1a1a1a]/10 bg-white">
      {/* mock meeting header */}
      <div className="bg-[#1a1a1a] text-white px-4 py-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#ff5f15]">// MTG-#### · {styleLabel}</div>
        <div className="font-display font-black text-lg mt-1 flex items-center gap-2">
          <Video className="w-4 h-4" />
          {template.title || "(untitled template)"}
        </div>
        <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">
          {template.duration_minutes || 60} min · save-the-date + join-now emails auto-fire
        </div>
      </div>
      <div className="p-4 space-y-3">
        {template.description && (
          <div className="text-[12px] text-[#1a1a1a]/75 italic">{template.description}</div>
        )}
        <div>
          <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#d6a900] font-bold mb-1.5">
            // Additional Agenda · {agenda.length} topic{agenda.length === 1 ? "" : "s"}
          </div>
          {agenda.length === 0 ? (
            <div className="text-[11px] text-[#1a1a1a]/45 italic">No baked-in agenda items yet — attendees can add ad-hoc topics during the meeting.</div>
          ) : (
            <ol className="space-y-1">
              {agenda.map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-[12px] text-[#1a1a1a] px-2 py-1 rounded-lg bg-[#faf8f4] border border-[#1a1a1a]/06">
                  <span className="font-mono font-bold text-[10px] text-[#1a1a1a]/50">#{i + 1}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/45">
          When loaded from the library, all fields become editable per-meeting.
        </div>
      </div>
    </div>
  );
}
